// File: src/MathTools/RationalGraphTool.js
//----------------------------------------------------------
// A "centered-axes" rational function plotter.
// 1) We parse `equation` into a JS function f(x).
// 2) We assume xRange includes 0, so x=0 => width/2 on screen.
// 3) Similarly yRange includes 0 => y=0 => height/2 on screen.
// 4) Axes drawn crossing at (0,0). Ticks on each axis.

import React, { useRef, useEffect } from "react";
import * as d3 from "d3";

export default function RationalGraphTool({
  equation = "(100 + 5*x)/x",
  width = 600,
  height = 400,
  xRange = [-10, 10],   // domain for x
  yRange = [-100, 100], // domain for y
  tickCount = 6
}) {
  const svgRef = useRef(null);

  // Helper to parse user eqn => f(x)
  function buildFunction(eqn) {
    const safe = eqn.replace(/\^/g, "**");
    try {
      // eslint-disable-next-line no-new-func
      return new Function("x", `return (${safe});`);
    } catch (err) {
      console.error("Equation parse error:", err);
      return () => 0;
    }
  }

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // 1) xScale => domain xRange => range [0,width],
    //    so x=0 => (0 - xMin)/(xMax - xMin)*width => if xRange is symmetric, that is width/2.
    const xScale = d3.scaleLinear()
      .domain(xRange)
      .range([0, width]);

    // 2) yScale => domain yRange => range [height,0],
    //    so y=0 => some fraction => typically height/2 if symmetric about 0.
    const yScale = d3.scaleLinear()
      .domain(yRange)
      .range([height, 0]);

    // 3) xAxis => at y=0 => yScale(0) => center horizontally
    const xAxis = d3.axisBottom(xScale).ticks(tickCount);
    const xAxisGroup = svg.append("g")
      .attr("transform", `translate(0, ${yScale(0)})`)
      .call(xAxis);

    xAxisGroup.selectAll("text")
      .style("font-size", "14px")
      .style("fill", "#333");

    // 4) yAxis => at x=0 => xScale(0) => center vertically
    const yAxis = d3.axisLeft(yScale).ticks(tickCount);
    const yAxisGroup = svg.append("g")
      .attr("transform", `translate(${xScale(0)},0)`)
      .call(yAxis);

    yAxisGroup.selectAll("text")
      .style("font-size", "14px")
      .style("fill", "#333");

    // 5) Build the function from the equation
    const f = buildFunction(equation);

    // 6) Sample points across xRange
    const sampleCount = 300;
    const dx = (xRange[1] - xRange[0]) / sampleCount;
    const points = [];
    for (let i=0; i<=sampleCount; i++){
      const xVal = xRange[0] + i*dx;
      let yVal;
      try {
        yVal = f(xVal);
        if (!Number.isFinite(yVal)) continue;
        points.push({ x:xVal, y:yVal });
      } catch {
        // skip
      }
    }

    // 7) D3 line generator
    const lineGen = d3.line()
      .x(d => xScale(d.x))
      .y(d => yScale(d.y));

    svg.append("path")
      .datum(points)
      .attr("fill","none")
      .attr("stroke","blue")
      .attr("stroke-width",2)
      .attr("d", lineGen);

    // 8) Label
    svg.append("text")
      .attr("x", xScale(xRange[0])+5)
      .attr("y", yScale(yRange[1]) + 15)
      .style("font-size","14px")
      .text(`y = ${equation}`);

  }, [equation, width, height, xRange, yRange, tickCount]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{ border:"1px solid #ccc", background:"#fff" }}
    />
  );
}
